/**
 * Process-wide registry of live AHP relays, keyed by task id. Ensures all
 * consumers for the same task share ONE websocket + one event sink, ref-counted
 * so the connection is torn down only after the last consumer releases it (with
 * a short grace period to survive remounts).
 *
 * Also acts as the single source of truth for "is a relay active for this
 * task", so every consumer can disable its fallback data source consistently.
 *
 * The sink that receives projected events and the Mission Control fetcher are
 * injected by the consumer, keeping this package free of any host application's
 * data layer.
 */

import type {ChatState, ActionEnvelope} from '@microsoft/agent-host-protocol'
import {RelayConnectionRuntime, type RelayLogger, type RelayStatus} from './relay-connection-runtime'
import type {MCConnectResponse, MCFetcher} from './mission-control/mission-control-environment-client'

const DISPOSE_GRACE_MS = 5000

/**
 * Surfaces relay connection diagnostics to the browser console. The runtime
 * otherwise swallows connect/subscribe failures behind a noop logger, which
 * makes a silently-reconnecting relay impossible to debug from the page.
 */
const relayLogger: RelayLogger = {
  // eslint-disable-next-line no-console
  info: message => console.info(`[ahp-relay] ${message}`),
  // eslint-disable-next-line no-console
  error: message => console.error(`[ahp-relay] ${message}`),
}

/** Receives the relay's snapshot + live actions for one session. */
export interface RelaySessionSink {
  /** Take ownership of the consumer's event store before any events arrive. */
  start(): void
  applySnapshot(state: ChatState): void
  applyAction(env: ActionEnvelope): void
  dispose(): void
}

/**
 * A {@link RelaySessionSink} that also exposes its folded `ChatState` as an
 * external store, so consumers can bind to it with `useSyncExternalStore`.
 */
export interface ObservableRelaySink extends RelaySessionSink {
  subscribe(listener: () => void): () => void
  getSnapshot(): ChatState | undefined
}

interface RelayEntry {
  refCount: number
  runtime: RelayConnectionRuntime
  sink: RelaySessionSink
  sessionId: string
  environmentId: string
  channel?: string
  status: RelayStatus
  error?: string
  disposeTimer?: ReturnType<typeof setTimeout>
}

const entries = new Map<string, RelayEntry>()
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

export function subscribeRelayRegistry(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function isRelayActive(taskId: string | undefined): boolean {
  if (!taskId) return false
  // An entry that is still in its dispose grace period continues to own the
  // shared store, so consumers must keep their fallback source disabled until
  // it is actually torn down (avoids two sources writing the same key).
  return entries.has(taskId)
}

export function getRelayStatus(taskId: string | undefined): RelayStatus | 'idle' {
  if (!taskId) return 'idle'
  return entries.get(taskId)?.status ?? 'idle'
}

export function getRelayError(taskId: string | undefined): string | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.error
}

/**
 * Returns the live observable store for `taskId`, if the registered sink
 * exposes one.
 */
export function getRelayStore(taskId: string | undefined): ObservableRelaySink | undefined {
  if (!taskId) return undefined
  const sink = entries.get(taskId)?.sink
  return sink && 'getSnapshot' in sink ? (sink as ObservableRelaySink) : undefined
}

export interface AcquireRelayOptions {
  taskId: string
  environmentId: string
  sessionId: string
  /** Channel URI to observe. Defaults to `ahp-session:/<sessionId>`. */
  channel?: string
  /** Performs Mission Control requests for connect/reconnect. */
  fetcher: MCFetcher
  /** Builds the sink that receives this relay's snapshot + live actions. */
  createSink: (taskId: string) => RelaySessionSink
  /**
   * Optional pre-obtained `/connect` response, consumed once on first
   * establish instead of making a fresh `/connect` call. Used by flows that
   * already minted credentials (e.g. `/connect?create_session=true`) so the
   * websocket is opened against the original `client_id`'s WPS groups — a
   * second `/connect` would mint a new client_id and miss events the daemon
   * publishes to the first lane. Subsequent reconnects always use
   * `/reconnect?client_id=…` regardless.
   */
  preconnect?: MCConnectResponse
}

export function acquireRelay({
  taskId,
  environmentId,
  sessionId,
  channel,
  fetcher,
  createSink,
  preconnect,
}: AcquireRelayOptions): void {
  const existing = entries.get(taskId)
  if (existing) {
    // The registry is keyed by task id, but the relay is scoped to a specific
    // session + environment + channel. If any differs, the cached runtime points
    // at the wrong channel, so tear it down and recreate rather than reuse.
    if (existing.sessionId === sessionId && existing.environmentId === environmentId && existing.channel === channel) {
      if (existing.disposeTimer) {
        clearTimeout(existing.disposeTimer)
        existing.disposeTimer = undefined
      }
      existing.refCount += 1
      return
    }
    if (existing.disposeTimer) clearTimeout(existing.disposeTimer)
    entries.delete(taskId)
    existing.sink.dispose()
    void existing.runtime.dispose()
  }

  const sink = createSink(taskId)
  const entry: RelayEntry = {
    refCount: 1,
    sink,
    sessionId,
    environmentId,
    channel,
    status: 'idle',
    runtime: undefined as unknown as RelayConnectionRuntime,
  }

  // Hand cache ownership to the sink before any events arrive.
  sink.start()

  // When pre-seeded, the runtime's first establish reuses the supplied
  // response instead of calling /connect. The lambda is consumed at most
  // once: any later reconnect cycle goes through mcReconnect (default uses
  // the fetcher + the original client_id), so the URL change is automatic.
  const mcConnect = preconnect ? () => Promise.resolve(preconnect) : undefined

  const runtime = new RelayConnectionRuntime({
    environmentId,
    sessionId,
    fetcher,
    logger: relayLogger,
    ...(channel ? {channel} : {}),
    ...(mcConnect ? {mcConnect} : {}),
    onSnapshot: state => sink.applySnapshot(state),
    onAction: env => sink.applyAction(env),
    onStatus: (status, error) => {
      entry.status = status
      entry.error = error
      notify()
    },
  })
  entry.runtime = runtime
  entries.set(taskId, entry)
  notify()

  void runtime.connect()
}

/** Dispatch a follow-up user turn over the live relay for a task. */
export function dispatchRelayTurn(taskId: string | undefined, text: string): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  entry.runtime.dispatchTurn(text)
}

/**
 * Force the relay for `taskId` to drop and re-establish its subscription,
 * so the next `subscribe` returns a fresh snapshot from the daemon. Useful
 * when a new consumer mounts on top of an already-active runtime whose
 * sink's cache may have missed action notifications (e.g. because the
 * previous consumer was torn down mid-stream).
 *
 * No-op if no relay is registered for `taskId`.
 */
export function refreshRelaySnapshot(taskId: string | undefined): void {
  if (!taskId) return
  const entry = entries.get(taskId)
  if (!entry) return
  // Tolerate runtime stubs (e.g. test fakes) that don't implement refresh().
  if (typeof entry.runtime.refresh !== 'function') return
  void entry.runtime.refresh()
}

/**
 * Resolves once the relay for `taskId` reaches the `connected` state, so
 * callers that hold an `acquireRelay` ref can safely
 * {@link dispatchRelayTurn} immediately after.
 *
 * Transient `error` / `reconnecting` states are tolerated — the runtime
 * automatically retries with backoff (e.g. when subscribing arrives before
 * the daemon has registered the session). Only `closed` (terminal) or the
 * `timeoutMs` budget expiring cause this to reject.
 */
export function whenRelayConnected(taskId: string | undefined, timeoutMs = 30_000): Promise<void> {
  if (!taskId) return Promise.reject(new Error('no active relay for this task'))
  const initial = entries.get(taskId)
  if (!initial) return Promise.reject(new Error('no active relay for this task'))
  if (initial.status === 'connected') return Promise.resolve()

  return new Promise((resolve, reject) => {
    let settled = false
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      unsubscribe()
      reject(new Error(`relay did not connect within ${timeoutMs}ms`))
    }, timeoutMs)

    const unsubscribe = subscribeRelayRegistry(() => {
      if (settled) return
      const entry = entries.get(taskId)
      if (!entry) {
        settled = true
        clearTimeout(timer)
        unsubscribe()
        reject(new Error('relay was torn down before connecting'))
        return
      }
      if (entry.status === 'connected') {
        settled = true
        clearTimeout(timer)
        unsubscribe()
        resolve()
      } else if (entry.status === 'closed') {
        settled = true
        clearTimeout(timer)
        unsubscribe()
        reject(new Error(entry.error ?? 'relay closed before connecting'))
      }
      // `error` and `reconnecting` are transient — keep waiting; the runtime
      // schedules its own retry with backoff and we will see the next
      // `connected` (or `closed`) transition.
    })
  })
}

export function releaseRelay(taskId: string): void {
  const entry = entries.get(taskId)
  if (!entry) return
  entry.refCount -= 1
  if (entry.refCount > 0) return

  entry.disposeTimer = setTimeout(() => {
    const current = entries.get(taskId)
    if (!current || current.refCount > 0) return
    entries.delete(taskId)
    current.sink.dispose()
    void current.runtime.dispose()
    notify()
  }, DISPOSE_GRACE_MS)
}

/** Test-only: force-clear all relays without grace period. */
export function __resetRelayRegistry(): void {
  for (const [, entry] of entries) {
    if (entry.disposeTimer) clearTimeout(entry.disposeTimer)
    entry.sink.dispose()
    void entry.runtime.dispose()
  }
  entries.clear()
  notify()
}
